import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Checkbox,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Slide
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ChatIcon from '@mui/icons-material/Chat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MemoryIcon from '@mui/icons-material/Memory';
import ImageIcon from '@mui/icons-material/Image';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import CategoryIcon from '@mui/icons-material/Category';
import SchoolIcon from '@mui/icons-material/School';
import ForumIcon from '@mui/icons-material/Forum';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StarIcon from '@mui/icons-material/Star';
import PaletteIcon from '@mui/icons-material/Palette';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const API_BASE = 'https://turismo.nara.chat/api';

// Transición para diálogos (estilo Centro de Ayuda)
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ============================================
// ESTILOS DE TARJETAS - Patrón Dashboard
// ============================================

// Estilo base para tarjetas con glassmorphism
const getCardStyle = (gradient, shadowColor, isSelected = false) => ({
  p: 2,
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  boxShadow: isSelected
    ? `0 0 0 3px ${shadowColor}, 0 1px 3px rgba(0, 0, 0, 0.08)`
    : '0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04)',
  border: isSelected
    ? `2px solid ${shadowColor.replace('0.4', '0.8')}`
    : '1px solid #e2e8f0',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: gradient
  },
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: `0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px ${shadowColor}`
  },
  '&:active': {
    transform: 'translateY(-2px) scale(0.98)'
  }
});

// Estilo para iconos en tarjetas
const getIconBoxStyle = (gradient, shadowColor) => ({
  width: 44,
  height: 44,
  borderRadius: 3,
  background: gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: `0 8px 20px ${shadowColor}`,
  flexShrink: 0,
  '& svg': { fontSize: 22 }
});

const AdminPlanes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Tab actual
  const [currentTab, setCurrentTab] = useState(0);

  // Estado para selección múltiple de chatbots
  const [selectedChatbots, setSelectedChatbots] = useState([]);

  // Estado para confirmación de eliminación de chatbots
  const [deleteDialog, setDeleteDialog] = useState({ open: false, chatbots: [] });
  const [deleting, setDeleting] = useState(false);

  // Estado para editor de planes
  const [planDialog, setPlanDialog] = useState({ open: false, plan: null, isNew: false });
  const [planDialogTab, setPlanDialogTab] = useState(0);
  const defaultPlanForm = {
    name: '', descripcion: '', subtitulo: '',
    precio_mensual: 0, num_conversaciones: 100, num_tokens: 0,
    num_licencias: 2, num_chatbots: 1, num_whatsapp: 1,
    dias_memoria: 30,
    tiene_ocr_documentos: false, tiene_agente_hibrido: true,
    tiene_emr: true, tiene_analytics: true,
    tiene_analisis_imagenes: false, tiene_recordatorios: true,
    tiene_exportacion: false, tiene_base_conocimiento: true,
    tiene_diagnostico_ia: true, tiene_clasificacion_ia: true,
    tiene_aprendizaje_auto: true, tiene_contexto_sesiones: true,
    tiene_dashboard_conv: true, tiene_soporte_prioritario: false,
    tiene_soporte_dedicado: false,
    es_trial: false, dias_trial: 0, es_destacado: false,
    color_plan: '#64748b', gradiente_plan: '',
    texto_cta: 'Elegir', hereda_de: '', features_lista: '',
    activo: true, orden: 0
  };
  const [planForm, setPlanForm] = useState(defaultPlanForm);
  const [savingPlan, setSavingPlan] = useState(false);

  // Estado para eliminar plan
  const [deletePlanDialog, setDeletePlanDialog] = useState({ open: false, plan: null });
  const [deletingPlan, setDeletingPlan] = useState(false);

  // Estado para números WhatsApp
  const [numerosWhatsapp, setNumerosWhatsapp] = useState([]);
  const [loadingNumeros, setLoadingNumeros] = useState(false);
  const [numeroDialog, setNumeroDialog] = useState({ open: false, numero: null, isNew: false });
  const [numeroForm, setNumeroForm] = useState({
    numero_twilio: '',
    friendly_name: '',
    descripcion: '',
    twilio_account_sid: '',
    twilio_auth_token: ''
  });
  const [showToken, setShowToken] = useState(false);
  const [savingNumero, setSavingNumero] = useState(false);
  const [deleteNumeroDialog, setDeleteNumeroDialog] = useState({ open: false, numero: null });
  const [deletingNumero, setDeletingNumero] = useState(false);
  const [asignarDialog, setAsignarDialog] = useState({ open: false, numero: null });
  const [chatbotsDisponibles, setChatbotsDisponibles] = useState([]);

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    loadData();
  }, []);

  // Limpiar selección cuando cambia la empresa
  useEffect(() => {
    setSelectedChatbots([]);
  }, [selectedEmpresa]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [empresasRes, planesRes, numerosRes] = await Promise.all([
        fetch(`${API_BASE}/empresas/?user_id=${userId}`),
        fetch(`${API_BASE}/admin/planes?user_id=${userId}`),
        fetch(`${API_BASE}/admin/numeros-whatsapp`)
      ]);

      if (!empresasRes.ok) {
        if (empresasRes.status === 403) {
          setError('No tienes permisos para acceder. Solo administradores de NARA Technology.');
          return;
        }
        throw new Error('Error al cargar empresas');
      }

      const empresasData = await empresasRes.json();
      const planesData = await planesRes.json();
      const numerosData = await numerosRes.json();

      setEmpresas(empresasData.empresas || []);
      setPlanes(planesData.planes || []);
      setNumerosWhatsapp(numerosData.numeros || []);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar los datos. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadNumerosWhatsapp = async () => {
    setLoadingNumeros(true);
    try {
      const res = await fetch(`${API_BASE}/admin/numeros-whatsapp`);
      const data = await res.json();
      setNumerosWhatsapp(data.numeros || []);
    } catch (err) {
      console.error('Error cargando números:', err);
    } finally {
      setLoadingNumeros(false);
    }
  };

  const handlePlanChange = async (chatbotId, chatbotName, newPlanId) => {
    setSaving(chatbotId);
    try {
      const res = await fetch(`${API_BASE}/chatbots/${chatbotId}/plan?plan_id=${newPlanId}&user_id=${userId}`, {
        method: 'PUT'
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al asignar plan');
      }

      const data = await res.json();

      // Actualizar localmente
      setEmpresas(prev => prev.map(emp => ({
        ...emp,
        chatbots: emp.chatbots.map(cb =>
          cb.id === chatbotId
            ? { ...cb, id_plan: newPlanId, plan: data.chatbot.plan }
            : cb
        )
      })));

      // Actualizar empresa seleccionada
      if (selectedEmpresa) {
        setSelectedEmpresa(prev => ({
          ...prev,
          chatbots: prev.chatbots.map(cb =>
            cb.id === chatbotId
              ? { ...cb, id_plan: newPlanId, plan: data.chatbot.plan }
              : cb
          )
        }));
      }

      setSnackbar({
        open: true,
        message: `Plan "${data.chatbot.plan.name}" asignado a "${chatbotName}"`,
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al asignar el plan',
        severity: 'error'
      });
    } finally {
      setSaving(null);
    }
  };

  // Toggle selección de chatbot
  const toggleChatbotSelection = (chatbot) => {
    setSelectedChatbots(prev => {
      const isSelected = prev.some(cb => cb.id === chatbot.id);
      if (isSelected) {
        return prev.filter(cb => cb.id !== chatbot.id);
      } else {
        return [...prev, chatbot];
      }
    });
  };

  // Seleccionar/deseleccionar todos
  const toggleSelectAll = () => {
    if (selectedEmpresa?.chatbots) {
      if (selectedChatbots.length === selectedEmpresa.chatbots.length) {
        setSelectedChatbots([]);
      } else {
        setSelectedChatbots([...selectedEmpresa.chatbots]);
      }
    }
  };

  const handleDeleteClick = (chatbot) => {
    setDeleteDialog({ open: true, chatbots: [chatbot] });
  };

  const handleDeleteSelectedClick = () => {
    if (selectedChatbots.length > 0) {
      setDeleteDialog({ open: true, chatbots: selectedChatbots });
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.chatbots.length === 0) return;

    setDeleting(true);
    try {
      const deletePromises = deleteDialog.chatbots.map(chatbot =>
        fetch(`${API_BASE}/flows/${chatbot.id}`, { method: 'DELETE' })
      );

      const results = await Promise.all(deletePromises);
      const failedCount = results.filter(r => !r.ok).length;

      if (failedCount > 0) {
        throw new Error(`Error al eliminar ${failedCount} chatbot(s)`);
      }

      const deletedIds = deleteDialog.chatbots.map(cb => cb.id);

      setEmpresas(prev => prev.map(emp => ({
        ...emp,
        chatbots: emp.chatbots.filter(cb => !deletedIds.includes(cb.id)),
        chatbots_count: emp.chatbots.filter(cb => !deletedIds.includes(cb.id)).length
      })));

      if (selectedEmpresa) {
        setSelectedEmpresa(prev => ({
          ...prev,
          chatbots: prev.chatbots.filter(cb => !deletedIds.includes(cb.id)),
          chatbots_count: prev.chatbots.filter(cb => !deletedIds.includes(cb.id)).length
        }));
      }

      setSelectedChatbots([]);

      const message = deleteDialog.chatbots.length === 1
        ? `Chatbot "${deleteDialog.chatbots[0].name}" eliminado`
        : `${deleteDialog.chatbots.length} chatbots eliminados`;

      setSnackbar({ open: true, message, severity: 'success' });
      setDeleteDialog({ open: false, chatbots: [] });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al eliminar chatbot(s)',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  // ===== FUNCIONES PARA GESTIÓN DE PLANES =====

  const openNewPlanDialog = () => {
    setPlanForm({ ...defaultPlanForm });
    setPlanDialogTab(0);
    setPlanDialog({ open: true, plan: null, isNew: true });
  };

  const openEditPlanDialog = (plan) => {
    setPlanForm({
      name: plan.name || '',
      descripcion: plan.descripcion || '',
      subtitulo: plan.subtitulo || '',
      precio_mensual: plan.precio_mensual || 0,
      num_conversaciones: plan.num_conversaciones || 100,
      num_tokens: plan.num_tokens || 0,
      num_licencias: plan.num_licencias || 2,
      num_chatbots: plan.num_chatbots || 1,
      num_whatsapp: plan.num_whatsapp || 1,
      dias_memoria: plan.dias_memoria || 30,
      tiene_ocr_documentos: !!plan.tiene_ocr_documentos,
      tiene_agente_hibrido: plan.tiene_agente_hibrido !== false,
      tiene_emr: plan.tiene_emr !== false,
      tiene_analytics: !!plan.tiene_analytics,
      tiene_analisis_imagenes: !!plan.tiene_analisis_imagenes,
      tiene_recordatorios: plan.tiene_recordatorios !== false,
      tiene_exportacion: !!plan.tiene_exportacion,
      tiene_base_conocimiento: plan.tiene_base_conocimiento !== false,
      tiene_diagnostico_ia: plan.tiene_diagnostico_ia !== false,
      tiene_clasificacion_ia: plan.tiene_clasificacion_ia !== false,
      tiene_aprendizaje_auto: plan.tiene_aprendizaje_auto !== false,
      tiene_contexto_sesiones: plan.tiene_contexto_sesiones !== false,
      tiene_dashboard_conv: plan.tiene_dashboard_conv !== false,
      tiene_soporte_prioritario: !!plan.tiene_soporte_prioritario,
      tiene_soporte_dedicado: !!plan.tiene_soporte_dedicado,
      es_trial: !!plan.es_trial,
      dias_trial: plan.dias_trial || 0,
      es_destacado: !!plan.es_destacado,
      color_plan: plan.color_plan || '#64748b',
      gradiente_plan: plan.gradiente_plan || '',
      texto_cta: plan.texto_cta || 'Elegir',
      hereda_de: plan.hereda_de || '',
      features_lista: plan.features_lista || '',
      activo: plan.activo !== false,
      orden: plan.orden || 0
    });
    setPlanDialogTab(0);
    setPlanDialog({ open: true, plan, isNew: false });
  };

  const handleSavePlan = async () => {
    if (!planForm.name.trim()) {
      setSnackbar({ open: true, message: 'El nombre del plan es requerido', severity: 'error' });
      return;
    }

    setSavingPlan(true);
    try {
      const params = new URLSearchParams({ user_id: userId });
      // Add all planForm fields to params
      Object.entries(planForm).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, value);
        }
      });

      let res;
      if (planDialog.isNew) {
        res = await fetch(`${API_BASE}/admin/planes?${params}`, { method: 'POST' });
      } else {
        res = await fetch(`${API_BASE}/admin/planes/${planDialog.plan.id}?${params}`, { method: 'PUT' });
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al guardar el plan');
      }

      const data = await res.json();

      // Actualizar lista de planes
      if (planDialog.isNew) {
        setPlanes(prev => [...prev, data.plan]);
      } else {
        setPlanes(prev => prev.map(p => p.id === data.plan.id ? data.plan : p));
      }

      setSnackbar({
        open: true,
        message: planDialog.isNew ? 'Plan creado exitosamente' : 'Plan actualizado exitosamente',
        severity: 'success'
      });
      setPlanDialog({ open: false, plan: null, isNew: false });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al guardar el plan',
        severity: 'error'
      });
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlanClick = (plan) => {
    setDeletePlanDialog({ open: true, plan });
  };

  const handleDeletePlanConfirm = async () => {
    if (!deletePlanDialog.plan) return;

    setDeletingPlan(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/planes/${deletePlanDialog.plan.id}?user_id=${userId}`,
        { method: 'DELETE' }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al eliminar el plan');
      }

      setPlanes(prev => prev.filter(p => p.id !== deletePlanDialog.plan.id));
      setSnackbar({
        open: true,
        message: `Plan "${deletePlanDialog.plan.name}" eliminado`,
        severity: 'success'
      });
      setDeletePlanDialog({ open: false, plan: null });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al eliminar el plan',
        severity: 'error'
      });
    } finally {
      setDeletingPlan(false);
    }
  };

  // ===== FUNCIONES PARA GESTIÓN DE NÚMEROS WHATSAPP =====

  const openNewNumeroDialog = () => {
    setNumeroForm({
      numero_twilio: '',
      friendly_name: '',
      descripcion: '',
      twilio_account_sid: '',
      twilio_auth_token: ''
    });
    setShowToken(false);
    setNumeroDialog({ open: true, numero: null, isNew: true });
  };

  const openEditNumeroDialog = (numero) => {
    setNumeroForm({
      numero_twilio: numero.numero_twilio,
      friendly_name: numero.friendly_name || '',
      descripcion: numero.descripcion || '',
      twilio_account_sid: numero.twilio_account_sid_display ? (numero.twilio_account_sid_display.endsWith('...') ? '' : numero.twilio_account_sid_display) : '',
      twilio_auth_token: ''
    });
    setShowToken(false);
    setNumeroDialog({ open: true, numero, isNew: false });
  };

  const handleSaveNumero = async () => {
    if (!numeroForm.numero_twilio.trim()) {
      setSnackbar({ open: true, message: 'El número de WhatsApp es requerido', severity: 'error' });
      return;
    }

    setSavingNumero(true);
    try {
      let res;
      if (numeroDialog.isNew) {
        res = await fetch(`${API_BASE}/admin/numeros-whatsapp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(numeroForm)
        });
      } else {
        const updatePayload = {
          friendly_name: numeroForm.friendly_name,
          descripcion: numeroForm.descripcion
        };
        // Only send credentials if user filled them in
        if (numeroForm.twilio_account_sid) {
          updatePayload.twilio_account_sid = numeroForm.twilio_account_sid;
          if (numeroForm.twilio_auth_token) {
            updatePayload.twilio_auth_token = numeroForm.twilio_auth_token;
          }
        } else {
          // If SID is empty, clear credentials
          updatePayload.twilio_account_sid = '';
          updatePayload.twilio_auth_token = '';
        }
        res = await fetch(`${API_BASE}/admin/numeros-whatsapp/${numeroDialog.numero.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload)
        });
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al guardar el número');
      }

      await loadNumerosWhatsapp();
      setSnackbar({
        open: true,
        message: numeroDialog.isNew ? 'Número agregado al inventario' : 'Número actualizado',
        severity: 'success'
      });
      setNumeroDialog({ open: false, numero: null, isNew: false });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al guardar el número',
        severity: 'error'
      });
    } finally {
      setSavingNumero(false);
    }
  };

  const handleDeleteNumeroClick = (numero) => {
    setDeleteNumeroDialog({ open: true, numero });
  };

  const handleDeleteNumeroConfirm = async () => {
    if (!deleteNumeroDialog.numero) return;

    setDeletingNumero(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/numeros-whatsapp/${deleteNumeroDialog.numero.id}`,
        { method: 'DELETE' }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al eliminar el número');
      }

      await loadNumerosWhatsapp();
      setSnackbar({
        open: true,
        message: `Número eliminado del inventario`,
        severity: 'success'
      });
      setDeleteNumeroDialog({ open: false, numero: null });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al eliminar el número',
        severity: 'error'
      });
    } finally {
      setDeletingNumero(false);
    }
  };

  const handleOpenAsignarDialog = async (numero) => {
    // Cargar chatbots que no tienen número asignado
    try {
      const allChatbots = empresas.flatMap(emp => emp.chatbots || []);
      const chatbotsSinNumero = allChatbots.filter(cb => {
        const tieneNumero = numerosWhatsapp.some(n => n.chatbot_id === cb.id);
        return !tieneNumero;
      });
      setChatbotsDisponibles(chatbotsSinNumero);
      setAsignarDialog({ open: true, numero });
    } catch (err) {
      setSnackbar({ open: true, message: 'Error cargando chatbots', severity: 'error' });
    }
  };

  const handleAsignarNumero = async (chatbotId) => {
    if (!asignarDialog.numero) return;

    try {
      const res = await fetch(
        `${API_BASE}/admin/numeros-whatsapp/${asignarDialog.numero.id}/asignar`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatbot_id: chatbotId })
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al asignar número');
      }

      await loadNumerosWhatsapp();
      setSnackbar({
        open: true,
        message: 'Número asignado correctamente',
        severity: 'success'
      });
      setAsignarDialog({ open: false, numero: null });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al asignar número',
        severity: 'error'
      });
    }
  };

  const handleDesvincularNumero = async (numero) => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/numeros-whatsapp/${numero.id}/desvincular`,
        { method: 'POST' }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al desvincular número');
      }

      await loadNumerosWhatsapp();
      setSnackbar({
        open: true,
        message: 'Número desvinculado correctamente',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error al desvincular número',
        severity: 'error'
      });
    }
  };

  // Busca color del plan desde los datos de la BD, con fallback
  const getPlanColor = (planNameOrObj) => {
    const fallback = { bg: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' };
    if (!planNameOrObj) return fallback;
    const name = typeof planNameOrObj === 'string' ? planNameOrObj : planNameOrObj.name;
    const found = planes.find(p => p.name === name);
    if (found && found.color_plan) {
      return {
        bg: found.color_plan,
        gradient: found.gradiente_plan || `linear-gradient(135deg, ${found.color_plan} 0%, ${found.color_plan} 100%)`
      };
    }
    return fallback;
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f1f5f9',
        gap: 2
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
        <Typography sx={{ color: '#64748b' }}>Cargando datos...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        minHeight: '100vh',
        p: 4,
        bgcolor: '#f1f5f9'
      }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ color: '#1e293b', mb: 3 }}>
          Volver al Dashboard
        </Button>
        <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto' }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f1f5f9',
      p: { xs: 2, md: 3 }
    }}>
      {/* Header */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#1e293b', fontWeight: 700 }}>
              Administracion de Planes
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              Gestiona planes y chatbots
            </Typography>
          </Box>
          <Tooltip title="Actualizar datos">
            <IconButton onClick={loadData} sx={{ color: '#64748b', '&:hover': { color: '#1e293b' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          sx={{
            '& .MuiTab-root': {
              color: '#64748b',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 40,
              '&.Mui-selected': { color: '#1e3a5f' }
            },
            '& .MuiTabs-indicator': { bgcolor: '#1e3a5f' }
          }}
        >
          <Tab label="Asignar Planes a Chatbots" />
          <Tab label="Gestionar Planes" />
          <Tab label="Números WhatsApp" />
        </Tabs>
      </Box>

      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* ===== TAB 0: ASIGNAR PLANES A CHATBOTS ===== */}
        {currentTab === 0 && (
          <>
            {/* Planes disponibles - Chips */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                Planes:
              </Typography>
              {planes.map(plan => (
                <Tooltip
                  key={plan.id}
                  title={
                    <Box sx={{ fontSize: 11 }}>
                      <Box>${plan.precio_mensual || 0}/mes • {plan.num_conversaciones} conv</Box>
                      {plan.tiene_agente_hibrido && <Box>✓ Agente IA</Box>}
                      {plan.tiene_emr && <Box>✓ Booking</Box>}
                      {plan.tiene_analytics && <Box>✓ Analytics</Box>}
                      {plan.tiene_ocr_documentos && <Box>✓ OCR Documentos</Box>}
                      {plan.tiene_analisis_imagenes && <Box>✓ Análisis documentos</Box>}
                      {plan.tiene_soporte_dedicado && <Box>✓ Soporte dedicado</Box>}
                      {plan.es_trial && <Box>Trial {plan.dias_trial}d</Box>}
                    </Box>
                  }
                >
                  <Chip
                    label={plan.name}
                    size="small"
                    sx={{
                      background: getPlanColor(plan.name).gradient,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 11,
                      height: 24
                    }}
                  />
                </Tooltip>
              ))}
            </Box>

            {/* Buscador de empresa */}
            <Paper sx={{
              p: 2,
              mb: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <Typography sx={{ color: '#1e293b', fontWeight: 600, mb: 1.5, fontSize: 14, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                Buscar Empresa
              </Typography>
              <Autocomplete
                size="small"
                options={empresas}
                getOptionLabel={(option) => option.company || ''}
                value={selectedEmpresa}
                onChange={(event, newValue) => setSelectedEmpresa(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Escribe el nombre de la empresa..."
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon sx={{ color: '#64748b', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#1e293b',
                        bgcolor: '#f8fafc',
                        borderRadius: 1.5,
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#6366f1' },
                        '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <BusinessIcon sx={{ color: '#fff', fontSize: 16 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{option.company}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {option.city} • {option.chatbots_count} chatbot(s)
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No se encontraron empresas"
              />
            </Paper>

            {/* Empresa seleccionada */}
            {selectedEmpresa && (
              <Box>
                {/* Info de la empresa */}
                <Paper sx={{
                  p: 2,
                  mb: 2,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <BusinessIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                        {selectedEmpresa.company}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
                        {selectedEmpresa.city} • {selectedEmpresa.chatbots_count} chatbot(s)
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Barra de acciones */}
                {selectedEmpresa.chatbots && selectedEmpresa.chatbots.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={toggleSelectAll}
                        startIcon={selectedChatbots.length === selectedEmpresa.chatbots.length ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                        sx={{ color: '#1e293b', fontSize: 12, textTransform: 'none', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                      >
                        {selectedChatbots.length === selectedEmpresa.chatbots.length ? 'Deseleccionar' : 'Seleccionar todos'}
                      </Button>
                      {selectedChatbots.length > 0 && (
                        <Chip label={`${selectedChatbots.length} seleccionado(s)`} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.1)', color: '#6366f1', fontSize: 11, height: 22 }} />
                      )}
                    </Box>
                    {selectedChatbots.length > 0 && (
                      <Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteSelectedClick}
                        sx={{ fontSize: 12, textTransform: 'none', borderRadius: 1.5, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        Eliminar seleccionados
                      </Button>
                    )}
                  </Box>
                )}

                {/* Grid de chatbots */}
                {selectedEmpresa.chatbots && selectedEmpresa.chatbots.length > 0 ? (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)', xl: 'repeat(5, 1fr)' },
                    gap: 2
                  }}>
                    {selectedEmpresa.chatbots.map((chatbot) => {
                      const isSelected = selectedChatbots.some(cb => cb.id === chatbot.id);
                      const gradient = chatbot.flow_type === 'intelligent'
                        ? 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)'
                        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
                      const shadowColor = chatbot.flow_type === 'intelligent'
                        ? 'rgba(168, 85, 247, 0.4)'
                        : 'rgba(99, 102, 241, 0.4)';

                      return (
                        <Box
                          key={chatbot.id}
                          onClick={() => toggleChatbotSelection(chatbot)}
                          sx={getCardStyle(
                            chatbot.plan ? getPlanColor(chatbot.plan.name).gradient : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            shadowColor,
                            isSelected
                          )}
                        >
                          {/* Header */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0, mt: 0.5, color: '#cbd5e1', '&.Mui-checked': { color: '#6366f1' } }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleChatbotSelection(chatbot)}
                            />
                            <Box sx={getIconBoxStyle(gradient, shadowColor)}>
                              <SmartToyIcon />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={chatbot.name}>
                                <Typography sx={{
                                  color: '#1e293b',
                                  fontWeight: 700,
                                  fontSize: 13,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {chatbot.name}
                                </Typography>
                              </Tooltip>
                              <Typography sx={{ color: '#64748b', fontSize: 10 }}>
                                ID: {chatbot.id}
                              </Typography>
                            </Box>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(chatbot); }}
                                sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Badges */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={chatbot.flow_type === 'intelligent' ? 'IA' : 'Tradicional'}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 9,
                                fontWeight: 700,
                                bgcolor: chatbot.flow_type === 'intelligent' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                                color: chatbot.flow_type === 'intelligent' ? '#a855f7' : '#6366f1'
                              }}
                            />
                            {chatbot.whatsapp_number && (
                              <Chip
                                icon={<WhatsAppIcon sx={{ fontSize: '12px !important', color: '#25D366 !important' }} />}
                                label={chatbot.whatsapp_number.replace('whatsapp:', '').slice(-4)}
                                size="small"
                                sx={{ height: 20, fontSize: 9, bgcolor: 'rgba(37, 211, 102, 0.1)', color: '#16a34a' }}
                              />
                            )}
                          </Box>

                          {/* Plan y selector */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {chatbot.plan ? (
                              <Chip
                                label={chatbot.plan.name}
                                size="small"
                                sx={{
                                  background: getPlanColor(chatbot.plan.name).gradient,
                                  color: '#fff',
                                  fontWeight: 700,
                                  fontSize: 10,
                                  height: 22,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                              />
                            ) : (
                              <Chip
                                label="Sin plan"
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                                  color: '#ef4444',
                                  fontWeight: 700,
                                  fontSize: 10,
                                  height: 22
                                }}
                              />
                            )}
                            <FormControl size="small" sx={{ flex: 1, minWidth: 80 }}>
                              <Select
                                value={chatbot.id_plan || ''}
                                onChange={(e) => { e.stopPropagation(); handlePlanChange(chatbot.id, chatbot.name, e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={saving === chatbot.id}
                                displayEmpty
                                sx={{
                                  color: '#1e293b',
                                  bgcolor: 'rgba(255,255,255,0.8)',
                                  borderRadius: 2,
                                  fontSize: 11,
                                  height: 28,
                                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' },
                                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                                  '.MuiSelect-select': { py: 0.5 }
                                }}
                              >
                                <MenuItem value="" disabled sx={{ fontSize: 11 }}><em>Cambiar...</em></MenuItem>
                                {planes.map(plan => (
                                  <MenuItem key={plan.id} value={plan.id} sx={{ fontSize: 11 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPlanColor(plan.name).bg }} />
                                      {plan.name}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {saving === chatbot.id && <CircularProgress size={16} sx={{ color: '#6366f1' }} />}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    background: '#fff',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 4,
                    border: '1px solid #e2e8f0'
                  }}>
                    <SmartToyIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ color: '#64748b', fontSize: 14 }}>Esta empresa no tiene chatbots</Typography>
                  </Box>
                )}
              </Box>
            )}

            {!selectedEmpresa && (
              <Box sx={{
                p: 5,
                textAlign: 'center',
                background: '#fff',
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                border: '1px solid #e2e8f0'
              }}>
                <BusinessIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
                <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600, mb: 0.5 }}>
                  Selecciona una empresa
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                  Usa el buscador para encontrar y gestionar los planes de sus chatbots
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* ===== TAB 1: GESTIONAR PLANES ===== */}
        {currentTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                Planes configurados ({planes.length})
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNewPlanDialog}
                sx={{
                  bgcolor: '#10b981',
                  fontSize: 12,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  '&:hover': { bgcolor: '#059669' }
                }}
              >
                Nuevo Plan
              </Button>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 2
            }}>
              {planes.map(plan => {
                const planColor = getPlanColor(plan.name);
                const shadowColor = planColor.bg.replace(')', ', 0.4)').replace('rgb', 'rgba').replace('#', 'rgba(').replace(/([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i, (_, r, g, b) => `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`);
                return (
                  <Box
                    key={plan.id}
                    sx={getCardStyle(planColor.gradient, `rgba(99, 102, 241, 0.4)`)}
                  >
                    {/* Header con icono */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                      <Box sx={getIconBoxStyle(planColor.gradient, `rgba(99, 102, 241, 0.3)`)}>
                        <AttachMoneyIcon />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>
                          {plan.name}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 11, lineHeight: 1.3 }}>
                          {plan.descripcion || 'Sin descripción'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditPlanDialog(plan); }}
                            sx={{ color: '#6366f1', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' } }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeletePlanClick(plan); }}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Métricas destacadas */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: 24, fontWeight: 800, background: planColor.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                          ${plan.precio_mensual || 0}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 10 }}>por mes</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#1e293b' }}>
                          {plan.num_conversaciones}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 10 }}>conv/mes</Typography>
                      </Box>
                    </Box>

                    {/* Info adicional */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      {plan.es_trial && (
                        <Chip label={`Trial ${plan.dias_trial || 15}d`} size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(100, 116, 139, 0.15)', color: '#64748b', fontWeight: 600 }} />
                      )}
                      {plan.es_destacado && (
                        <Chip icon={<StarIcon sx={{ fontSize: '12px !important', color: '#f59e0b !important' }} />} label="Destacado" size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', fontWeight: 600 }} />
                      )}
                      {plan.dias_memoria > 0 && (
                        <Chip icon={<MemoryIcon sx={{ fontSize: '12px !important', color: '#6366f1 !important' }} />} label={`${plan.dias_memoria}d mem`} size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(99, 102, 241, 0.15)', color: '#6366f1', fontWeight: 600 }} />
                      )}
                    </Box>

                    {/* Características como badges */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {plan.tiene_agente_hibrido && (
                        <Chip icon={<PsychologyIcon sx={{ fontSize: '12px !important', color: '#10b981 !important' }} />} label="IA"
                          size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 600 }} />
                      )}
                      {plan.tiene_emr && (
                        <Chip icon={<FolderSharedIcon sx={{ fontSize: '12px !important', color: '#0ea5e9 !important' }} />} label="Booking"
                          size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(14, 165, 233, 0.15)', color: '#0ea5e9', fontWeight: 600 }} />
                      )}
                      {plan.tiene_analytics && (
                        <Chip icon={<AnalyticsIcon sx={{ fontSize: '12px !important', color: '#8b5cf6 !important' }} />} label="Analytics"
                          size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6', fontWeight: 600 }} />
                      )}
                      {plan.tiene_ocr_documentos && (
                        <Chip icon={<DocumentScannerIcon sx={{ fontSize: '12px !important', color: '#f59e0b !important' }} />} label="OCR"
                          size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', fontWeight: 600 }} />
                      )}
                      {plan.tiene_analisis_imagenes && (
                        <Chip icon={<ImageIcon sx={{ fontSize: '12px !important', color: '#ec4899 !important' }} />} label="Imágenes"
                          size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(236, 72, 153, 0.15)', color: '#ec4899', fontWeight: 600 }} />
                      )}
                      {plan.tiene_soporte_dedicado && (
                        <Chip icon={<SupportAgentIcon sx={{ fontSize: '12px !important', color: '#14b8a6 !important' }} />} label="Soporte+"
                          size="small" sx={{ fontSize: 9, height: 20, bgcolor: 'rgba(20, 184, 166, 0.15)', color: '#14b8a6', fontWeight: 600 }} />
                      )}
                    </Box>

                    {/* Estado activo/inactivo */}
                    {!plan.activo && (
                      <Chip label="INACTIVO" size="small" sx={{ mt: 1, fontSize: 9, height: 20, bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', fontWeight: 700 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ===== TAB 2: NÚMEROS WHATSAPP ===== */}
        {currentTab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography sx={{ color: '#1e293b', fontWeight: 600, fontSize: 14 }}>
                  Numeros WhatsApp ({numerosWhatsapp.length})
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: 11 }}>
                  {numerosWhatsapp.filter(n => n.asignado).length} asignados • {numerosWhatsapp.filter(n => !n.asignado).length} disponibles
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNewNumeroDialog}
                sx={{
                  bgcolor: '#25D366',
                  fontSize: 12,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  '&:hover': { bgcolor: '#128C7E' }
                }}
              >
                Agregar Número
              </Button>
            </Box>

            {loadingNumeros ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#fff' }} />
              </Box>
            ) : numerosWhatsapp.length === 0 ? (
              <Box sx={{
                p: 5,
                textAlign: 'center',
                background: '#fff',
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: '0 8px 24px rgba(37, 211, 102, 0.3)'
                }}>
                  <WhatsAppIcon sx={{ fontSize: 36, color: '#fff' }} />
                </Box>
                <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600, mb: 0.5 }}>
                  No hay números configurados
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                  Agrega un número de Twilio para comenzar a vincular chatbots
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2
              }}>
                {numerosWhatsapp.map(numero => {
                  const gradient = numero.asignado
                    ? 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
                    : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)';
                  const shadowColor = numero.asignado ? 'rgba(37, 211, 102, 0.4)' : 'rgba(100, 116, 139, 0.4)';

                  return (
                    <Box
                      key={numero.id}
                      sx={getCardStyle(gradient, shadowColor)}
                    >
                      {/* Header con icono */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Box sx={getIconBoxStyle(gradient, shadowColor)}>
                          <WhatsAppIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>
                            {numero.friendly_name || 'Sin nombre'}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: 11, fontFamily: 'monospace' }}>
                            {numero.numero_twilio.replace('whatsapp:', '')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditNumeroDialog(numero); }}
                              sx={{ color: '#6366f1', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {!numero.asignado && (
                            <Tooltip title="Eliminar">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteNumeroClick(numero); }}
                                sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {/* Credenciales custom badge */}
                      {numero.has_custom_credentials && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <VpnKeyIcon sx={{ fontSize: 12, color: '#6366f1' }} />
                          <Typography sx={{ fontSize: 10, color: '#6366f1', fontWeight: 600 }}>
                            Cuenta: {numero.twilio_account_sid_display || 'Personalizada'}
                          </Typography>
                        </Box>
                      )}

                      {/* Descripción */}
                      {numero.descripcion && (
                        <Typography sx={{ color: '#64748b', fontSize: 11, mb: 2, lineHeight: 1.4 }}>
                          {numero.descripcion}
                        </Typography>
                      )}

                      {/* Estado de asignación */}
                      {numero.asignado ? (
                        <Box>
                          <Box sx={{
                            bgcolor: 'rgba(37, 211, 102, 0.08)',
                            p: 1.5,
                            borderRadius: 2,
                            mb: 1.5,
                            border: '1px solid rgba(37, 211, 102, 0.2)'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#25D366', boxShadow: '0 0 8px rgba(37, 211, 102, 0.6)' }} />
                              <Typography sx={{ color: '#25D366', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                                Asignado
                              </Typography>
                            </Box>
                            <Typography sx={{ color: '#1e293b', fontSize: 12, fontWeight: 600 }}>
                              {numero.chatbot_nombre}
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: 10 }}>
                              {numero.chatbot_empresa || 'Sin empresa'}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LinkOffIcon />}
                            onClick={(e) => { e.stopPropagation(); handleDesvincularNumero(numero); }}
                            sx={{
                              width: '100%',
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: 2,
                              borderColor: '#ef4444',
                              color: '#ef4444',
                              py: 0.75,
                              '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239, 68, 68, 0.08)' }
                            }}
                          >
                            Desvincular
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <Box sx={{
                            bgcolor: 'rgba(100, 116, 139, 0.08)',
                            p: 1.5,
                            borderRadius: 2,
                            mb: 1.5,
                            border: '1px solid rgba(100, 116, 139, 0.2)',
                            textAlign: 'center'
                          }}>
                            <Typography sx={{ color: '#64748b', fontSize: 11 }}>
                              Disponible para asignar
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LinkIcon />}
                            onClick={(e) => { e.stopPropagation(); handleOpenAsignarDialog(numero); }}
                            sx={{
                              width: '100%',
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 0.75,
                              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                              boxShadow: '0 4px 14px rgba(37, 211, 102, 0.4)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                boxShadow: '0 6px 20px rgba(37, 211, 102, 0.5)'
                              }
                            }}
                          >
                            Asignar a Chatbot
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Dialog para eliminar chatbots */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ open: false, chatbots: [] })} PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444', fontWeight: 700, fontSize: 16, pb: 1 }}>
          <WarningIcon sx={{ fontSize: 20 }} />
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', mb: 1.5, fontSize: 13 }}>
            {deleteDialog.chatbots.length === 1 ? '¿Eliminar este chatbot?' : `¿Eliminar ${deleteDialog.chatbots.length} chatbots?`}
          </Typography>
          <Box sx={{ maxHeight: 150, overflow: 'auto', bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fecaca', p: 1.5 }}>
            {deleteDialog.chatbots.map((chatbot, index) => (
              <Box key={chatbot.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: index < deleteDialog.chatbots.length - 1 ? '1px solid #fecaca' : 'none' }}>
                <SmartToyIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 12, flex: 1 }}>{chatbot.name}</Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>ID: {chatbot.id}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: '#ef4444', fontSize: 11, mt: 1.5, fontWeight: 500 }}>Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 1 }}>
          <Button onClick={() => setDeleteDialog({ open: false, chatbots: [] })} disabled={deleting} size="small" sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, color: '#64748b', fontSize: 12 }}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} disabled={deleting} variant="contained" size="small" sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', fontSize: 12, '&:hover': { bgcolor: '#dc2626' } }}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear/editar plan */}
      <Dialog
        open={planDialog.open}
        onClose={() => !savingPlan && setPlanDialog({ open: false, plan: null, isNew: false })}
        TransitionComponent={SlideTransition}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxHeight: '90vh'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', gap: 2
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '12px',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <AttachMoneyIcon sx={{ color: '#fbbf24', fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
              {planDialog.isNew ? 'Crear Nuevo Plan' : `Editar Plan: ${planForm.name}`}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
              Configura todos los aspectos del plan
            </Typography>
          </Box>
          {planForm.color_plan && (
            <Box sx={{
              width: 24, height: 24, borderRadius: '50%',
              background: planForm.gradiente_plan || planForm.color_plan,
              border: '2px solid rgba(255,255,255,0.4)'
            }} />
          )}
        </Box>

        {/* Tabs inside dialog */}
        <Tabs
          value={planDialogTab}
          onChange={(e, v) => setPlanDialogTab(v)}
          sx={{
            borderBottom: '1px solid #e2e8f0', px: 2, minHeight: 40,
            '& .MuiTab-root': {
              fontSize: 12, fontWeight: 600, textTransform: 'none',
              minHeight: 40, py: 1,
              '&.Mui-selected': { color: '#1e3a5f' }
            },
            '& .MuiTabs-indicator': { bgcolor: '#1e3a5f' }
          }}
        >
          <Tab label="General" />
          <Tab label="Funcionalidades" />
          <Tab label="Presentación" />
        </Tabs>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5, maxHeight: '55vh', overflow: 'auto' }}>

            {/* Tab 0: General */}
            {planDialogTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Nombre" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} size="small" fullWidth required
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <FormControlLabel
                    control={<Switch checked={planForm.activo} onChange={(e) => setPlanForm({ ...planForm, activo: e.target.checked })} size="small" />}
                    label={<Typography sx={{ fontSize: 12, fontWeight: 600 }}>Activo</Typography>}
                    sx={{ minWidth: 90 }}
                  />
                </Box>
                <TextField label="Subtítulo (landing)" value={planForm.subtitulo} onChange={(e) => setPlanForm({ ...planForm, subtitulo: e.target.value })} size="small" fullWidth placeholder="Para operadores turísticos"
                  sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                <TextField label="Descripción" value={planForm.descripcion} onChange={(e) => setPlanForm({ ...planForm, descripcion: e.target.value })} size="small" fullWidth multiline rows={2}
                  sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />

                <Divider />
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5 }}>Límites</Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                  <TextField label="Precio USD/mes" type="number" value={planForm.precio_mensual} onChange={(e) => setPlanForm({ ...planForm, precio_mensual: parseFloat(e.target.value) || 0 })} size="small"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <TextField label="Conversaciones/mes" type="number" value={planForm.num_conversaciones} onChange={(e) => setPlanForm({ ...planForm, num_conversaciones: parseInt(e.target.value) || 0 })} size="small"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <TextField label="Tokens IA" type="number" value={planForm.num_tokens} onChange={(e) => setPlanForm({ ...planForm, num_tokens: parseInt(e.target.value) || 0 })} size="small"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                  <TextField label="Licencias" type="number" value={planForm.num_licencias} onChange={(e) => setPlanForm({ ...planForm, num_licencias: parseInt(e.target.value) || 0 })} size="small"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <TextField label="Chatbots" type="number" value={planForm.num_chatbots} onChange={(e) => setPlanForm({ ...planForm, num_chatbots: parseInt(e.target.value) || 0 })} size="small"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <TextField label="WhatsApp" type="number" value={planForm.num_whatsapp} onChange={(e) => setPlanForm({ ...planForm, num_whatsapp: parseInt(e.target.value) || 0 })} size="small"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <TextField label="Memoria (días)" type="number" value={planForm.dias_memoria} onChange={(e) => setPlanForm({ ...planForm, dias_memoria: parseInt(e.target.value) || 0 })} size="small"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                </Box>

                <Divider />
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5 }}>Trial</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControlLabel
                    control={<Switch checked={planForm.es_trial} onChange={(e) => setPlanForm({ ...planForm, es_trial: e.target.checked })} size="small" />}
                    label={<Typography sx={{ fontSize: 13 }}>Es plan de prueba</Typography>}
                  />
                  {planForm.es_trial && (
                    <TextField label="Días de trial" type="number" value={planForm.dias_trial} onChange={(e) => setPlanForm({ ...planForm, dias_trial: parseInt(e.target.value) || 0 })} size="small" sx={{ width: 140,
                      '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 1: Funcionalidades */}
            {planDialogTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { title: 'IA y Turismo', items: [
                    { key: 'tiene_diagnostico_ia', label: 'Consulta de disponibilidad IA', icon: <MedicalInformationIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_clasificacion_ia', label: 'Clasificación inteligente', icon: <CategoryIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_analisis_imagenes', label: 'Análisis de documentos', icon: <ImageIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_ocr_documentos', label: 'OCR de documentos', icon: <DocumentScannerIcon sx={{ fontSize: 16 }} /> },
                  ]},
                  { title: 'Gestión Turística', items: [
                    { key: 'tiene_agente_hibrido', label: 'Reservaciones automáticas (Agente IA)', icon: <PsychologyIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_recordatorios', label: 'Recordatorios automáticos', icon: <NotificationsIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_emr', label: 'Gestión de booking', icon: <FolderSharedIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_base_conocimiento', label: 'Base de conocimiento', icon: <MenuBookIcon sx={{ fontSize: 16 }} /> },
                  ]},
                  { title: 'Memoria e Inteligencia', items: [
                    { key: 'tiene_aprendizaje_auto', label: 'Aprendizaje automático', icon: <SchoolIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_contexto_sesiones', label: 'Contexto entre sesiones', icon: <ForumIcon sx={{ fontSize: 16 }} /> },
                  ]},
                  { title: 'Analytics y Soporte', items: [
                    { key: 'tiene_dashboard_conv', label: 'Dashboard de conversaciones', icon: <DashboardIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_analytics', label: 'Analytics avanzados', icon: <AnalyticsIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_exportacion', label: 'Exportación de datos', icon: <FileDownloadIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_soporte_prioritario', label: 'Soporte prioritario', icon: <SupportAgentIcon sx={{ fontSize: 16 }} /> },
                    { key: 'tiene_soporte_dedicado', label: 'Soporte dedicado', icon: <StarIcon sx={{ fontSize: 16 }} /> },
                  ]},
                ].map((section) => (
                  <Box key={section.title}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                      {section.title}
                    </Typography>
                    <Box sx={{
                      display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.5,
                      bgcolor: '#f8fafc', borderRadius: 2, p: 1, border: '1px solid #e2e8f0'
                    }}>
                      {section.items.map((item) => (
                        <FormControlLabel
                          key={item.key}
                          control={<Switch checked={planForm[item.key]} onChange={(e) => setPlanForm({ ...planForm, [item.key]: e.target.checked })} size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ color: planForm[item.key] ? '#10b981' : '#94a3b8' }}>{item.icon}</Box>
                              <Typography sx={{ fontSize: 12, color: planForm[item.key] ? '#1e293b' : '#94a3b8' }}>{item.label}</Typography>
                            </Box>
                          }
                          sx={{ mx: 0 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Tab 2: Presentación (Landing Page) */}
            {planDialogTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Apariencia en Landing Page
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={planForm.es_destacado} onChange={(e) => setPlanForm({ ...planForm, es_destacado: e.target.checked })} size="small" />}
                    label={<Typography sx={{ fontSize: 13 }}>Plan destacado (Más Popular)</Typography>}
                  />
                  <TextField label="Orden" type="number" value={planForm.orden} onChange={(e) => setPlanForm({ ...planForm, orden: parseInt(e.target.value) || 0 })} size="small" sx={{ width: 100,
                    '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Color del plan" value={planForm.color_plan} onChange={(e) => setPlanForm({ ...planForm, color_plan: e.target.value })} size="small" fullWidth placeholder="#f59e0b"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: planForm.color_plan, border: '1px solid #e2e8f0' }} /></InputAdornment>
                    }}
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                  <TextField label="Texto del botón CTA" value={planForm.texto_cta} onChange={(e) => setPlanForm({ ...planForm, texto_cta: e.target.value })} size="small" fullWidth placeholder="Elegir Pro"
                    sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />
                </Box>

                <TextField label="Gradiente CSS" value={planForm.gradiente_plan} onChange={(e) => setPlanForm({ ...planForm, gradiente_plan: e.target.value })} size="small" fullWidth placeholder="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                  InputProps={{
                    startAdornment: planForm.gradiente_plan ? <InputAdornment position="start"><Box sx={{ width: 24, height: 16, borderRadius: 1, background: planForm.gradiente_plan, border: '1px solid #e2e8f0' }} /></InputAdornment> : null
                  }}
                  sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />

                <TextField label="Hereda de (plan padre)" value={planForm.hereda_de} onChange={(e) => setPlanForm({ ...planForm, hereda_de: e.target.value })} size="small" fullWidth placeholder="Demo, Básico..."
                  helperText='Muestra "Todo lo del plan X, más:" en el landing'
                  sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }} />

                <Divider />
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Features (lista de la tarjeta)
                </Typography>
                <TextField
                  label="Features JSON"
                  value={planForm.features_lista}
                  onChange={(e) => setPlanForm({ ...planForm, features_lista: e.target.value })}
                  size="small" fullWidth multiline rows={4}
                  placeholder='["150 conversaciones/mes","Memoria semántica 90 días","Soporte prioritario"]'
                  helperText="Array JSON con las features que se muestran en la tarjeta del plan"
                  sx={{ '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}
                />

                {/* Preview */}
                {planForm.features_lista && (() => {
                  try {
                    const features = JSON.parse(planForm.features_lista);
                    if (Array.isArray(features) && features.length > 0) {
                      return (
                        <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, border: '1px solid #e2e8f0' }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', mb: 1 }}>
                            <VisibilityIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} /> Vista previa
                          </Typography>
                          {features.map((f, i) => (
                            <Typography key={i} sx={{ fontSize: 12, color: '#334155', mb: 0.3 }}>
                              ✓ {f}
                            </Typography>
                          ))}
                        </Box>
                      );
                    }
                  } catch { /* invalid JSON, ignore */ }
                  return null;
                })()}
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Actions */}
        <Box sx={{
          px: 2.5, py: 2,
          display: 'flex', gap: 1.5,
          borderTop: '1px solid rgba(0,0,0,0.06)'
        }}>
          <Button
            onClick={() => setPlanDialog({ open: false, plan: null, isNew: false })}
            disabled={savingPlan}
            fullWidth
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              color: '#64748b', bgcolor: '#f1f5f9', py: 1,
              '&:hover': { bgcolor: '#e2e8f0' }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSavePlan}
            disabled={savingPlan}
            fullWidth
            variant="contained"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              bgcolor: '#1e3a5f', py: 1,
              boxShadow: '0 4px 14px 0 rgba(30, 58, 95, 0.39)',
              '&:hover': { bgcolor: '#2d5a87', boxShadow: '0 6px 20px rgba(30, 58, 95, 0.45)' }
            }}
          >
            {savingPlan ? <CircularProgress size={20} color="inherit" /> : (planDialog.isNew ? 'Crear Plan' : 'Guardar Cambios')}
          </Button>
        </Box>
      </Dialog>

      {/* Dialog para eliminar plan */}
      <Dialog open={deletePlanDialog.open} onClose={() => !deletingPlan && setDeletePlanDialog({ open: false, plan: null })} PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444', fontWeight: 700, fontSize: 16, pb: 1 }}>
          <WarningIcon sx={{ fontSize: 20 }} />
          Eliminar Plan
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', mb: 1.5, fontSize: 13 }}>
            ¿Estás seguro de que deseas eliminar el plan?
          </Typography>
          {deletePlanDialog.plan && (
            <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fecaca' }}>
              <Chip label={deletePlanDialog.plan.name} sx={{ background: getPlanColor(deletePlanDialog.plan.name).gradient, color: '#fff', fontWeight: 700, fontSize: 12 }} />
            </Box>
          )}
          <Typography sx={{ color: '#ef4444', fontSize: 11, mt: 1.5, fontWeight: 500 }}>
            Solo se puede eliminar si ningún chatbot lo está usando.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 1 }}>
          <Button onClick={() => setDeletePlanDialog({ open: false, plan: null })} disabled={deletingPlan} size="small" sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, color: '#64748b', fontSize: 12 }}>Cancelar</Button>
          <Button onClick={handleDeletePlanConfirm} disabled={deletingPlan} variant="contained" size="small" sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', fontSize: 12, '&:hover': { bgcolor: '#dc2626' } }}>
            {deletingPlan ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear/editar número WhatsApp - Diseño Compacto Moderno */}
      <Dialog
        open={numeroDialog.open}
        onClose={() => !savingNumero && setNumeroDialog({ open: false, numero: null, isNew: false })}
        TransitionComponent={SlideTransition}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        {/* Header compacto con icono destacado */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <WhatsAppIcon sx={{ color: '#fbbf24', fontSize: 22 }} />
          </Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, flex: 1 }}>
            {numeroDialog.isNew ? 'Nuevo Número' : 'Editar Número'}
          </Typography>
          {!numeroDialog.isNew && (
            <Chip
              label="Activo"
              size="small"
              sx={{
                height: 22,
                bgcolor: 'rgba(74, 222, 128, 0.2)',
                color: '#4ade80',
                fontSize: 10,
                fontWeight: 700
              }}
            />
          )}
        </Box>

        {/* Formulario compacto */}
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Número de WhatsApp */}
          <TextField
            label="Número WhatsApp"
            placeholder="whatsapp:+573001234567"
            value={numeroForm.numero_twilio}
            onChange={(e) => setNumeroForm({ ...numeroForm, numero_twilio: e.target.value })}
            size="small"
            fullWidth
            required
            disabled={!numeroDialog.isNew}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: numeroDialog.isNew ? '#1e3a5f' : '#9ca3af', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: numeroDialog.isNew ? '#fff' : '#f8fafc',
                fontSize: 13,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' }
              },
              '& .MuiInputLabel-root': { fontSize: 13 },
              '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }
            }}
          />

          {/* Nombre Amigable */}
          <TextField
            label="Nombre (opcional)"
            placeholder="Línea Principal, Urgencias..."
            value={numeroForm.friendly_name}
            onChange={(e) => setNumeroForm({ ...numeroForm, friendly_name: e.target.value })}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#fff',
                fontSize: 13,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' }
              },
              '& .MuiInputLabel-root': { fontSize: 13 },
              '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }
            }}
          />

          {/* Descripción - solo 2 líneas */}
          <TextField
            label="Descripción (opcional)"
            placeholder="Notas adicionales..."
            value={numeroForm.descripcion}
            onChange={(e) => setNumeroForm({ ...numeroForm, descripcion: e.target.value })}
            size="small"
            fullWidth
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#fff',
                fontSize: 13,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' }
              },
              '& .MuiInputLabel-root': { fontSize: 13 },
              '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' }
            }}
          />

          {/* Sección de credenciales Twilio */}
          <Box sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(99, 102, 241, 0.04)',
            border: '1px solid rgba(99, 102, 241, 0.15)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <VpnKeyIcon sx={{ fontSize: 16, color: '#6366f1' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                Credenciales Twilio
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#64748b', ml: 'auto' }}>
                Opcional
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1.5, lineHeight: 1.4 }}>
              Si este número pertenece a otra cuenta de Twilio, ingresa las credenciales aquí. Si se deja vacío, se usarán las credenciales por defecto.
            </Typography>
            {!numeroDialog.isNew && numeroDialog.numero?.has_custom_credentials && !numeroForm.twilio_account_sid && (
              <Box sx={{ mb: 1.5, p: 1, bgcolor: 'rgba(16, 185, 129, 0.08)', borderRadius: 1.5, border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                <Typography sx={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>
                  Este número tiene credenciales personalizadas configuradas ({numeroDialog.numero?.twilio_account_sid_display})
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  Ingresa nuevos valores para actualizarlas, o deja vacío para mantenerlas.
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="Account SID"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={numeroForm.twilio_account_sid}
                onChange={(e) => setNumeroForm({ ...numeroForm, twilio_account_sid: e.target.value })}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>AC</Typography>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fff',
                    fontSize: 12,
                    fontFamily: 'monospace',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
                  },
                  '& .MuiInputLabel-root': { fontSize: 12 },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' }
                }}
              />
              <TextField
                label="Auth Token"
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={numeroForm.twilio_auth_token}
                onChange={(e) => setNumeroForm({ ...numeroForm, twilio_auth_token: e.target.value })}
                size="small"
                fullWidth
                type={showToken ? 'text' : 'password'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowToken(!showToken)} sx={{ p: 0.5 }}>
                        {showToken ? <VisibilityOffIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> : <VisibilityIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fff',
                    fontSize: 12,
                    fontFamily: 'monospace',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
                  },
                  '& .MuiInputLabel-root': { fontSize: 12 },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' }
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Botones compactos */}
        <Box sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          gap: 1.5,
          borderTop: '1px solid rgba(0,0,0,0.06)'
        }}>
          <Button
            onClick={() => setNumeroDialog({ open: false, numero: null, isNew: false })}
            disabled={savingNumero}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b',
              bgcolor: '#f1f5f9',
              py: 1,
              '&:hover': { bgcolor: '#e2e8f0' }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveNumero}
            disabled={savingNumero}
            fullWidth
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#1e3a5f',
              py: 1,
              boxShadow: '0 4px 14px 0 rgba(30, 58, 95, 0.39)',
              '&:hover': { bgcolor: '#2d5a87', boxShadow: '0 6px 20px rgba(30, 58, 95, 0.45)' }
            }}
          >
            {savingNumero ? <CircularProgress size={20} color="inherit" /> : (numeroDialog.isNew ? 'Agregar' : 'Guardar')}
          </Button>
        </Box>
      </Dialog>

      {/* Dialog para eliminar número WhatsApp */}
      <Dialog open={deleteNumeroDialog.open} onClose={() => !deletingNumero && setDeleteNumeroDialog({ open: false, numero: null })} PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444', fontWeight: 700, fontSize: 16, pb: 1 }}>
          <WarningIcon sx={{ fontSize: 20 }} />
          Eliminar Número
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', mb: 1.5, fontSize: 13 }}>
            ¿Estás seguro de que deseas eliminar este número del inventario?
          </Typography>
          {deleteNumeroDialog.numero && (
            <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fecaca' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 12 }}>
                    {deleteNumeroDialog.numero.friendly_name || 'Sin nombre'}
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: 11 }}>
                    {deleteNumeroDialog.numero.numero_twilio}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Typography sx={{ color: '#ef4444', fontSize: 11, mt: 1.5, fontWeight: 500 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 1 }}>
          <Button onClick={() => setDeleteNumeroDialog({ open: false, numero: null })} disabled={deletingNumero} size="small" sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, color: '#64748b', fontSize: 12 }}>Cancelar</Button>
          <Button onClick={handleDeleteNumeroConfirm} disabled={deletingNumero} variant="contained" size="small" sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', fontSize: 12, '&:hover': { bgcolor: '#dc2626' } }}>
            {deletingNumero ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para asignar número a chatbot - Estilo Centro de Ayuda */}
      <Dialog
        open={asignarDialog.open}
        onClose={() => setAsignarDialog({ open: false, numero: null })}
        TransitionComponent={SlideTransition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "#f8fafc",
            boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
            overflow: 'hidden'
          }
        }}
      >
        {/* Header con gradiente azul - Estilo Centro de Ayuda */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2.5,
            px: 3
          }}
        >
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LinkIcon sx={{ color: "#fbbf24", fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
              Asignar Número a Chatbot
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
              Vincula este número con un asistente virtual
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Número seleccionado - Estilo Centro de Ayuda */}
          {asignarDialog.numero && (
            <Box sx={{
              p: 2,
              mx: 3,
              mt: 3,
              bgcolor: '#eff6ff',
              borderRadius: 2,
              border: '1px solid #bfdbfe',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#1e3a5f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <WhatsAppIcon sx={{ color: '#fbbf24', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#1e40af', fontSize: 14 }}>
                  {asignarDialog.numero.friendly_name || 'Sin nombre'}
                </Typography>
                <Typography sx={{ color: '#3b82f6', fontSize: 12 }}>
                  {asignarDialog.numero.numero_twilio}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Lista de chatbots */}
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f', mb: 2, textTransform: 'uppercase' }}>
              Selecciona un chatbot
            </Typography>

            {chatbotsDisponibles.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  fontSize: 13,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { alignItems: 'center' }
                }}
              >
                No hay chatbots disponibles sin número asignado.
              </Alert>
            ) : (
              <Box sx={{
                maxHeight: 280,
                overflow: 'auto',
                bgcolor: '#fff',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                {chatbotsDisponibles.map((chatbot, idx) => (
                  <Box
                    key={chatbot.id}
                    onClick={() => handleAsignarNumero(chatbot.id)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderBottom: idx < chatbotsDisponibles.length - 1 ? '1px solid #e2e8f0' : 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(30, 58, 95, 0.05)',
                        '& .link-icon': { opacity: 1, transform: 'translateX(0)' }
                      }
                    }}
                  >
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <SmartToyIcon sx={{ color: '#1e3a5f', fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
                        {chatbot.name}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: 11 }}>
                        ID: {chatbot.id}
                      </Typography>
                    </Box>
                    <Box
                      className="link-icon"
                      sx={{
                        opacity: 0.5,
                        transform: 'translateX(-5px)',
                        transition: 'all 0.2s'
                      }}
                    >
                      <LinkIcon sx={{ color: '#1e3a5f', fontSize: 20 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{
          borderTop: "1px solid #e2e8f0",
          background: "#fff",
          px: 3,
          py: 2
        }}>
          <Button
            onClick={() => setAsignarDialog({ open: false, numero: null })}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b',
              borderColor: '#e2e8f0',
              px: 3
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 1.5, fontSize: 13 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPlanes;
